import { useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

export function DrawerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const isOpen = element.variant === "open" || element.visible === true || element.visible === "true"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  // Determine side: "left" or "right", default to "left"
  const side = element.variant === "right" ? "right" : "left"

  const handleClose = useCallback(() => {
    dispatchEvent(element.id, "close", {})
  }, [element.id, dispatchEvent])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose()
      }
    },
    [handleClose],
  )

  if (!isOpen) return null

  const sideClasses = side === "right"
    ? "inset-y-0 right-0 border-l"
    : "inset-y-0 left-0 border-r"

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid={`drawer-${element.id}`}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80"
        onClick={handleOverlayClick}
      />
      {/* Drawer panel */}
      <div
        className={cn(
          "fixed z-50 flex h-full w-72 flex-col bg-background shadow-lg",
          sideClasses,
          element.className,
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          {text && (
            <h2 className="text-lg font-semibold leading-none tracking-tight">
              {text}
            </h2>
          )}
          <button
            type="button"
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            onClick={handleClose}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        {/* Navigation items / children */}
        <nav className="flex-1 overflow-y-auto p-4">
          {element.children?.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </nav>
      </div>
    </div>
  )
}
